import {Button} from '@dabafinance/react-components';
import {Table, Tag} from 'antd';
import {useNavigate} from 'react-router-dom';
import styles from './userTable.module.scss';
import gamer from 'images/gamer.png';
import UserLink from 'components/userLink/UserLink';
import moment from 'moment';

const formatRegionEnumLabel = value =>
  value
    ? String(value)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase())
    : null;

const UserTable = ({data, loading, pagination, operations}) => {
  const navigate = useNavigate();

  const columns = [
    {
      title: 'Name',
      dataIndex: 'firstName',
      key: 'name',
      render: (text, record) => (
        <div className={styles['record-label']}>
          <div className={styles['record-name']}>
            {record.imageUrl ? (
              <img src={record.imageUrl} alt="" />
            ) : (
              <img src={gamer} alt="" />
            )}
            <h4 style={{width: 100}}>
              <UserLink userId={record.id} tab="overview">
                {record.firstName} {record.lastName}
              </UserLink>
            </h4>
          </div>
        </div>
      ),
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (text, record) => (
        <div className={styles['record-label']}>
          <h4>{record.username}</h4>
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text, record) => (
        <div className={styles['record-email']}>
          {/* Shown in full — an address is what an operator copies or checks
              against, so it wraps onto a second line rather than truncating. */}
          <h4>{record?.email || '--'}</h4>
        </div>
      ),
    },
    {
      title: 'Phone Number',
      dataIndex: 'expiring',
      key: 'expiring',
      render: (text, record) => (
        <div className={styles['record-label']}>
          <h4>{`${record?.countryCode ?? '--'}${
            record?.phoneNumber ?? '--'
          }`}</h4>
        </div>
      ),
    },
    {
      title: 'Profile Completion',
      dataIndex: 'profileCompleted',
      key: 'invested',
      render: (text, record) => (
        <div>
          <span>{record?.profileCompletion?.completionPercentage}%</span>
        </div>
      ),
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      key: 'joined',
      render: (text, record) => (
        <div className={styles['record-label']}>
          <h4>{moment(record.createdAt).format('MMM Do YYYY')}</h4>
        </div>
      ),
    },
    {
      title: 'Joined From',
      dataIndex: 'joinedFrom',
      key: 'joinedFrom',
      render: (text, record) => {
        const origination = (
          record?.source ||
          record?.currentSessionDetails?.medium ||
          ''
        ).toUpperCase();
        const isWeb = origination === 'WEBSITE' || origination === 'WEB';
        const label = isWeb
          ? 'Web'
          : origination
          ? origination.charAt(0) + origination.slice(1).toLowerCase()
          : 'App';
        return (
          <div className={styles['record-label']}>
            <Tag color={isWeb ? 'blue' : 'default'}>{label}</Tag>
          </div>
        );
      },
    },
    {
      title: 'Region',
      dataIndex: 'regionCategory',
      key: 'regionCategory',
      render: (_, record) => {
        const rc = record?.regionCategory;
        const region = formatRegionEnumLabel(rc?.region);
        const subRegion = formatRegionEnumLabel(rc?.subRegion);
        if (!region && !subRegion) {
          return (
            <div className={styles.regionCell}>
              <h4>—</h4>
            </div>
          );
        }
        return (
          <div className={styles.regionCell}>
            <h4>{region || '—'}</h4>
            {subRegion ? (
              <span className={styles.regionSubLabel}>{subRegion}</span>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Deleted Account',
      dataIndex: 'isDeleted',
      key: 'isDeleted',
      render: (text, record) => (
        <div className={styles['record-label']}>
          <Tag color={record?.isDeleted ? 'red' : 'green'}>
            {record?.isDeleted ? 'Yes' : 'No'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'operation',
      key: 'actions',
      render: (text, record) => (
        <Button
          onClick={() => navigate(`/dashboard/user/${record.id}/overview`)}
          type="secondary"
          disabled={!operations?.view}
          label="View details"
        />
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}></div>

      <div>
        <Table
          className={styles['user-table']}
          dataSource={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          onRow={record => ({
            onClick: () =>
              operations?.view &&
              navigate(`/dashboard/user/${record.id}/overview`),
            style: {cursor: operations?.view ? 'pointer' : 'default'},
          })}
        />
      </div>
    </div>
  );
};

export default UserTable;
